import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const CompanyContext = createContext(null);

export function CompanyProvider({ children, user }) {
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [companyId, setCompanyId] = useState(localStorage.getItem("agroport_company_id") || "");
  const [branchId, setBranchId] = useState(localStorage.getItem("agroport_branch_id") || "");
  const [loading, setLoading] = useState(false);

  const loadCompanies = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
      // auto-select first if none selected
      if (!companyId && data.length > 0) {
        const firstId = data[0].id;
        localStorage.setItem("agroport_company_id", firstId);
        setCompanyId(firstId);
      } else if (companyId && !data.find((c) => c.id === companyId)) {
        localStorage.removeItem("agroport_company_id");
        setCompanyId(data[0]?.id || "");
        if (data[0]) localStorage.setItem("agroport_company_id", data[0].id);
      }
    } finally { setLoading(false); }
  // eslint-disable-next-line
  }, [user]);

  const loadBranches = useCallback(async () => {
    if (!companyId) { setBranches([]); return; }
    try {
      const { data } = await api.get("/branches", { params: { company_id: companyId } });
      setBranches(data);
      // Sucursal emisora: la elegida; si no hay (o ya no existe), la predeterminada del usuario
      // o la única disponible. Cada documento se emite con los datos de su sucursal.
      if (!branchId || !data.find((b) => b.id === branchId)) {
        const def = data.find((b) => b.id === user?.sucursal_id) || (data.length === 1 ? data[0] : null);
        if (def) localStorage.setItem("agroport_branch_id", def.id);
        else localStorage.removeItem("agroport_branch_id");
        setBranchId(def ? def.id : "");
      }
    } catch {}
  // eslint-disable-next-line
  }, [companyId, user?.sucursal_id]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);
  useEffect(() => { loadBranches(); }, [loadBranches]);

  const switchCompany = (id) => {
    localStorage.setItem("agroport_company_id", id);
    localStorage.removeItem("agroport_branch_id");
    setCompanyId(id);
    setBranchId("");
    // force reload to refresh all data with new scope
    setTimeout(() => window.location.reload(), 150);
  };

  const switchBranch = (id) => {
    if (id) localStorage.setItem("agroport_branch_id", id);
    else localStorage.removeItem("agroport_branch_id");
    setBranchId(id);
    setTimeout(() => window.location.reload(), 150);
  };

  const current = companies.find((c) => c.id === companyId);
  const currentBranch = branches.find((b) => b.id === branchId);

  return (
    <CompanyContext.Provider value={{
      companies, branches, current, currentBranch,
      companyId, branchId, switchCompany, switchBranch,
      loading, refresh: loadCompanies
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => useContext(CompanyContext);
